import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

// ─── Types ───────────────────────────────────────────────────────────────────
interface Overview {
  cashReadiness?: {
    state: "ready" | "review";
    reason: string;
    policy: "cash_only";
  };
  hubAdvancePence?: number;
  enabled: boolean;
  minPence: number;
  maxPence: number;
  availablePence: number;
  heldPence: number;
  reservedPence: number;
  collectedPence: number;
  contributions: number;
  refunded: number;
  consumedPence: number;
  consumedCount: number;
  reinstatedPence: number;
}
interface Contribution {
  id: number;
  amountPence: number;
  remainingPence: number;
  displayPreference: string;
  donorDisplayName: string | null;
  status: string;
  originOrderId: number | null;
  storeName: string | null;
  createdAt: string;
}
interface Allocation {
  id: number;
  contributionId: number;
  recipientOrderId: number | null;
  amountPence: number;
  shortfallPence: number;
  reinstatedPence: number;
  status: string;
  createdAt: string;
}

const money = (p: number) => `£${(p / 100).toFixed(2)}`;
const fmt = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function AdminKindness() {
  useAuth();
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");
  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const [overview, setOverview] = useState<Overview | null>(null);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);

  const loadOverview = useCallback(async () => {
    const r = await fetch("/api/admin/kindness/overview", {
      headers: authHeaders,
    });
    if (r.ok) setOverview(await r.json());
  }, []);
  const loadContributions = useCallback(async () => {
    const r = await fetch("/api/admin/kindness/contributions", {
      headers: authHeaders,
    });
    if (r.ok) setContributions((await r.json()).contributions ?? []);
  }, []);
  const loadAllocations = useCallback(async () => {
    const r = await fetch("/api/admin/kindness/allocations", {
      headers: authHeaders,
    });
    if (r.ok) setAllocations((await r.json()).allocations ?? []);
  }, []);

  useEffect(() => {
    void loadOverview();
    void loadContributions();
    void loadAllocations();
  }, []);

  return (
    <AdminLayout>
      <div className="p-4 max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Random Act of Kindness</h1>
          <p className="text-sm text-gray-500">
            Customers can add a few pounds at checkout; the pot pays down other
            customers' orders.
          </p>
        </div>

        {overview && (
          <SettingsCard
            overview={overview}
            authHeaders={authHeaders}
            toast={toast}
            reload={loadOverview}
          />
        )}
        {overview && <BalancesRow o={overview} />}

        <ContributionsTable rows={contributions} />
        <AllocationsTable rows={allocations} />
      </div>
    </AdminLayout>
  );
}

// ─── Settings ────────────────────────────────────────────────────────────────
function SettingsCard({
  overview,
  authHeaders,
  toast,
  reload,
}: {
  overview: Overview;
  authHeaders: Record<string, string>;
  toast: ReturnType<typeof useToast>["toast"];
  reload: () => Promise<void>;
}) {
  // Min/max held as pounds strings for the inputs; converted to pence on save.
  const [minPounds, setMinPounds] = useState(
    (overview.minPence / 100).toFixed(2),
  );
  const [maxPounds, setMaxPounds] = useState(
    (overview.maxPence / 100).toFixed(2),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMinPounds((overview.minPence / 100).toFixed(2));
    setMaxPounds((overview.maxPence / 100).toFixed(2));
  }, [overview.minPence, overview.maxPence]);

  const put = async (body: Record<string, unknown>) => {
    const r = await fetch("/api/admin/kindness/settings", {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify(body),
    });
    if (r.ok) {
      await reload();
      return true;
    }
    toast({ title: "Save failed", variant: "destructive" });
    return false;
  };

  const toggleEnabled = async (enabled: boolean) => {
    const ok = await put({ enabled });
    if (ok)
      toast({ title: enabled ? "Kindness enabled" : "Kindness disabled" });
  };

  const saveLimits = async () => {
    const minPence = Math.round(Number(minPounds) * 100);
    const maxPence = Math.round(Number(maxPounds) * 100);
    if (
      !Number.isFinite(minPence) ||
      !Number.isFinite(maxPence) ||
      minPence < 0 ||
      maxPence < 0
    ) {
      toast({ title: "Enter valid amounts", variant: "destructive" });
      return;
    }
    if (maxPence < minPence) {
      toast({ title: "Max must be at least the min", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const ok = await put({ minPence, maxPence });
      if (ok) toast({ title: "Limits saved" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
          <div>
            <div className="font-medium text-sm">Enabled</div>
            <div className="text-xs text-amber-700">
              Kindness also requires verified cash custody before it can be offered at checkout.
            </div>
          </div>
          <Switch
            checked={overview.enabled}
            onCheckedChange={(v) => void toggleEnabled(v)}
          />
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <label className="block text-sm">
            <span className="text-slate-600">Minimum (£)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={minPounds}
              onChange={(e) => setMinPounds(e.target.value)}
              className="block border rounded px-2 py-1 w-28 mt-1"
            />
          </label>
          <label className="block text-sm">
            <span className="text-slate-600">Maximum (£)</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={maxPounds}
              onChange={(e) => setMaxPounds(e.target.value)}
              className="block border rounded px-2 py-1 w-28 mt-1"
            />
          </label>
          <Button onClick={() => void saveLimits()} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Balances ────────────────────────────────────────────────────────────────
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-xl font-bold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}

function BalancesRow({ o }: { o: Overview }) {
  return (
    <div>
      {o.cashReadiness?.state !== "ready" && (
        <p
          role="status"
          className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
        >
          New kindness credit is temporarily unavailable while the Hub checks
          its available payment balance. Donations can still be collected, and
          existing amounts owed to shops remain recorded for payment.
        </p>
      )}
      <p className="mb-3 text-sm text-slate-600">The Hub covers paid donations while Stripe clears them. Daily payouts continue. Donations are separate from Hub earnings.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <Stat label="Available" value={money(o.availablePence)} />
        <Stat label="Awaiting verification or funding" value={money(o.heldPence ?? 0)} />
        <Stat label="Hub advanced — awaiting Stripe" value={money(o.hubAdvancePence ?? 0)} />
        <Stat label="Reserved" value={money(o.reservedPence)} />
        <Stat label="Collected" value={money(o.collectedPence)} />
        <Stat label="Consumed" value={money(o.consumedPence)} />
        <Stat label="Reinstated" value={money(o.reinstatedPence)} />
        <Stat label="Contributions" value={String(o.contributions)} />
        <Stat label="Refunded" value={String(o.refunded)} />
      </div>
    </div>
  );
}

// ─── Contributions ───────────────────────────────────────────────────────────
function ContributionsTable({ rows }: { rows: Contribution[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Contributions</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">No contributions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Remaining</th>
                  <th className="py-2 pr-3">Donor</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Store</th>
                  <th className="py-2 pr-3">Order #</th>
                  <th className="py-2 pr-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{c.id}</td>
                    <td className="py-2 pr-3">{money(c.amountPence)}</td>
                    <td className="py-2 pr-3">{money(c.remainingPence)}</td>
                    <td className="py-2 pr-3">
                      {c.displayPreference === "named" ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            Named
                          </span>
                          <span className="text-slate-800">
                            {c.donorDisplayName || "(no name given)"}
                          </span>
                        </span>
                      ) : (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                          Anonymous
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">{c.status}</td>
                    <td className="py-2 pr-3">{c.storeName ?? "—"}</td>
                    <td className="py-2 pr-3">{c.originOrderId ?? "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {fmt(c.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Allocations ─────────────────────────────────────────────────────────────
function AllocationsTable({ rows }: { rows: Allocation[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Allocations</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">No allocations yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Contribution #</th>
                  <th className="py-2 pr-3">Recipient order #</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Shortfall</th>
                  <th className="py-2 pr-3">Reinstated</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{a.id}</td>
                    <td className="py-2 pr-3">{a.contributionId}</td>
                    <td className="py-2 pr-3">{a.recipientOrderId ?? "—"}</td>
                    <td className="py-2 pr-3">{money(a.amountPence)}</td>
                    <td className="py-2 pr-3">{money(a.shortfallPence)}</td>
                    <td className="py-2 pr-3">{money(a.reinstatedPence)}</td>
                    <td className="py-2 pr-3">{a.status}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {fmt(a.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
